import React, { useContext, useState, createContext, useEffect, useMemo, useCallback } from 'react';
import { useHistory } from 'react-router';
import { API } from '../api';
import { connectorProfileSchema } from '../lib/validationSchemas';
import { ConnectorProfile, ErrorCode, FileUploadResult, FilterOption, ImageProvider, LoadingStatus, UserNetwork } from '../types/types';
import { useUser } from './UserContext';
import { useAlert } from './AlertContext';
import { NetworkPagePathState } from '../types/router';
import { filterOptionsAreSame } from '../lib/utils';
import { ApiError, isApiError } from '../api/api';

interface ConnectorContextProviderProps {
  profile: ConnectorProfile,
  getProfileImageProvider: (id: number) => ImageProvider,
  fetchProfile: (profileId: number) => Promise<void>,
  updateProfile: (data: any) => Promise<void>,
  userNetworkList: UserNetwork[],
  userNetworkTotalCount: number,
  currentNetworkPage: number,
  networkPageSize: number,
  networkLoadingStatus: LoadingStatus,
  updateCurrentNetworkPage: (page: number) => void,
  canSendReferrals: boolean,
  editable: boolean,
  shouldShowIncompleteProfileGuide: boolean,
  networkFilterOptions: FilterOption[]
}

const defaultProfileInfo: ConnectorProfile = {
  id: -1,
  firstName: '',
  lastName: '',
  email: '',
  companyName: '',
  linkedInProfileUrl: '',
  introduction: '',
  profileImageUrl: '',
  referralExpectation: '',
  entrepreneurRevenueRequiredLow: 0,
  editable: false
};

const pageSize = 10;

export const ConnectorContext = createContext<ConnectorContextProviderProps>({
  profile: defaultProfileInfo,
  getProfileImageProvider: (id: number) => ({ imgUrl: '', editable: false }),
  fetchProfile: async (profileId: number) => {},
  updateProfile: async (data: any) => {},
  userNetworkList: [],
  userNetworkTotalCount: 0,
  currentNetworkPage: 0,
  networkPageSize: pageSize,
  networkLoadingStatus: LoadingStatus.Idle,
  updateCurrentNetworkPage: (page: number) => {},
  canSendReferrals: false,
  editable: false,
  shouldShowIncompleteProfileGuide: false,
  networkFilterOptions: []
});

export const useConnector = () => useContext(ConnectorContext);

interface ConnectorNetworkPageState {
  page: number,
  investors: UserNetwork[],
  filterOptions: FilterOption[]
}

export function ConnectorContextProvider(props: any) {
  const { saveProfileImage, getAccessToken, updateRoleData } = useUser();
  const [profile, setProfile] = useState<ConnectorProfile>(defaultProfileInfo);
  const [tempImgUrl, setTempImgUrl] = useState<string | null>(null);
  const [networkPageState, setNetworkPageState] = useState<ConnectorNetworkPageState>({ page: 0, investors: [], filterOptions: [] });
  const [userNetworkTotalCount, setUserNetworkTotalCount] = useState<number>(0);
  const [networkLoadingStatus, setNetworkLoadingStatus] = useState<LoadingStatus>(LoadingStatus.Idle);
  const { children } = props;
  const { postNewAlert } = useAlert();
  const history = useHistory<any>();
  const { state: pageState, pathname } = history.location;

  const updateCurrentNetworkPage = (page: number) => {
    history.push(pathname, { ...pageState, page });
  };

  const [currentNetworkPage, networkFilterOptions] = useMemo(() => {
    const castState = pageState as NetworkPagePathState | undefined;
    if (castState) {
      return [castState.page || 1, castState.networkFilterOptions || []];
    }
    return [1, []];
  }, [pageState]);

  const fetchProfile = useCallback(async (profileId: number) => {
    try {
      const accessToken = await getAccessToken();
      const profileData = await API.getConnectorProfile(accessToken, profileId);
      setProfile(profileData);
    } catch (e: unknown) {
      console.error('error fetching connector profile data: ', e);
      if (isApiError(e as any)) {
        const error = e as any as ApiError;
        if (error.code === ErrorCode.NoReadAccess) {
          history.replace('/');
          return;
        }
      }
      postNewAlert('An error occurred fetching the connector profile.');
    }
  }, [getAccessToken, history, postNewAlert]);

  const updateProfile = async (data: any) => {
    try {
      const accessToken = await getAccessToken();
      const profileData = await API.updateConnectorProfile(accessToken, data);
      setProfile(profileData);
    } catch (e: unknown) {
      console.error('error updating connector profile: ', e);
      postNewAlert('An error occurred updating the connector profile.');
    }
  };

  const updateProfileImage = async (image: FileUploadResult) => {
    setTempImgUrl(image.url);
    try {
      const newProfileUrl = await saveProfileImage(image);
      if (newProfileUrl) {
        setProfile({
          ...profile,
          profileImageUrl: newProfileUrl
        });
      }
    } catch (e: unknown) {
      console.error('error updating profile picture: ', e);
      postNewAlert('An error occurred updating the profile picture.');
    } finally {
      setTempImgUrl(null);
    }
  };

  const getProfileImageProvider = (id: number) => {
    const imgUrl = profile.profileImageUrl;
    if (profile.editable && id === profile.id) {
      return {
        imgUrl,
        updateImage: updateProfileImage,
        tempImgUrl,
        editable: true
      };
    }
    return { imgUrl, editable: false };
  };

  useEffect(() => {
    const fetchNetwork = async () => {
      setNetworkLoadingStatus(LoadingStatus.Loading);
      try {
        const token = await getAccessToken();
        const start = (currentNetworkPage - 1) * pageSize;
        const response = await API.getConnectorNetwork(token, start, pageSize, networkFilterOptions);
        setNetworkPageState({ page: currentNetworkPage, investors: response.network, filterOptions: networkFilterOptions });
        setUserNetworkTotalCount(response.total);
      } catch (e: unknown) {
        console.error('network error', e);
        postNewAlert('An error occurred fetching your network.');
        setNetworkPageState({ page: currentNetworkPage, investors: [], filterOptions: networkFilterOptions });
        setUserNetworkTotalCount(0);
      } finally {
        setNetworkLoadingStatus(LoadingStatus.Idle);
      }
    };
    const filtersChanged = !filterOptionsAreSame(networkPageState.filterOptions, networkFilterOptions);
    if ((networkPageState.page !== currentNetworkPage || filtersChanged) && networkLoadingStatus === LoadingStatus.Idle) {
      fetchNetwork();
    }
  }, [networkPageState, currentNetworkPage, networkFilterOptions, networkLoadingStatus, getAccessToken, postNewAlert]);

  const canSendReferrals = useMemo(() => {
    if (!profile.editable) { return false; }
    try {
      connectorProfileSchema.validateSync(profile);
      return true;
    } catch (e: unknown) {
      return false;
    }
  }, [profile]);

  const shouldShowIncompleteProfileGuide = !canSendReferrals && profile.editable;

  useEffect(() => {
    if (shouldShowIncompleteProfileGuide) {
      updateRoleData({ profileIncomplete: true, fullName: `${profile.firstName} ${profile.lastName}` });
    } else {
      updateRoleData({ profileIncomplete: false, fullName: `${profile.firstName} ${profile.lastName}` });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldShowIncompleteProfileGuide, profile]);

  return (
    <ConnectorContext.Provider value={{
      profile,
      getProfileImageProvider,
      fetchProfile,
      updateProfile,
      userNetworkList: networkPageState.investors,
      userNetworkTotalCount,
      currentNetworkPage,
      networkPageSize: pageSize,
      networkLoadingStatus,
      updateCurrentNetworkPage,
      canSendReferrals,
      editable: profile.editable,
      shouldShowIncompleteProfileGuide,
      networkFilterOptions
    }}
    >
      {children}
    </ConnectorContext.Provider>
  );
}
